import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { V, Haptic } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from '../components/Icons';
import { track, SectionTitle, onAccent } from '../components/kit';
import { useLayout } from '../utils/responsive';
import { Card, Btn, Field, Sheet, Chip, Progress, Stat, ValidationWarning, validateBody, SuccessToastCtrl, ConfirmCtrl } from '../components/ui';
import { today, ago, fmtShort, fmtFull, uid, wUnit, convW, toKg, toLbs, chartCfg } from '../utils/helpers';
import { Undo } from '../utils/undo';
import { ShareCard } from '../utils/share';
import { checkAndAwardMissions } from './gamification';

export function parseHeightToM(h){
  if(!h)return null;
  const hs=String(h).trim();
  const cm=hs.match(/^([\d.]+)\s*cm$/i);if(cm)return parseFloat(cm[1])/100;
  const fi=hs.match(/^(\d+)['\s](\d+)/);if(fi)return(parseInt(fi[1])*12+parseInt(fi[2]))*0.0254;
  const ff=hs.match(/^(\d+)'$/);if(ff)return parseInt(ff[1])*0.3048; // require trailing apostrophe for feet
  // Bare number: treat as inches if ≤96 (8ft), cm if >96
  const bare=hs.match(/^([\d.]+)$/);if(bare){const v=parseFloat(bare[1]);return v>96?v/100:v*0.0254;}
  return null;
}

// ─── US Navy body-fat estimate ───
// Measurements arrive in the user's display units (cm when units==="kg", inches when "lbs").
export function navyBF({sex,units,waist,neck,hips,heightM}){
  const w=parseFloat(waist)||0,n=parseFloat(neck)||0,h2=parseFloat(hips)||0;
  if(!w||!n||!heightM)return null;
  const toIn=(v)=>units==="kg"?v/2.54:v;
  const wIn=toIn(w),nIn=toIn(n),hIn=heightM/0.0254;
  const female=(sex||"").toLowerCase().startsWith("f");
  if(female&&!h2)return null;
  if(!female&&wIn-nIn<=0)return null;
  let bf;
  if(female)bf=163.205*Math.log10(wIn+toIn(h2)-nIn)-97.684*Math.log10(hIn)-78.387;
  else bf=86.010*Math.log10(wIn-nIn)-70.041*Math.log10(hIn)+36.76;
  if(!isFinite(bf)||bf<2||bf>60)return null;
  return Math.round(bf*10)/10;
}

// ─── Mifflin-St Jeor BMR + activity-scaled TDEE ───
const ACTIVITY_MULT={"Sedentary":1.2,"Lightly Active":1.375,"Moderate":1.55,"Very Active":1.725,"Extremely Active":1.9};
export function calcEnergy(profile,latestWeight,units){
  const hm=parseHeightToM(profile?.height);
  const w=parseFloat(latestWeight)||0;
  if(!hm||!w||!profile?.dob||!profile?.sex)return null;
  const age=Math.floor((Date.now()-new Date(profile.dob+"T00:00:00").getTime())/31557600000);
  if(age<10||age>110)return null;
  const wKg=units==="kg"?w:w*0.453592;
  const female=(profile.sex||"").toLowerCase().startsWith("f");
  const bmr=Math.round(10*wKg+6.25*(hm*100)-5*age+(female?-161:5));
  const mult=ACTIVITY_MULT[profile.activityLevel]||1.4;
  const tdee=Math.round(bmr*mult);
  return{bmr,tdee,age,mult,activity:profile.activityLevel||"Unknown"};
}

// All measurement fields — single source for form, comparison, history, and charts
const MEASUREMENTS=[
  {k:"waist",l:"Waist",c:"#06b6d4"},
  {k:"hips",l:"Hips",c:"#ec4899"},
  {k:"chest",l:"Chest",c:"#22d3ee"},
  {k:"shoulders",l:"Shoulders",c:"#818cf8"},
  {k:"neck",l:"Neck",c:"#94a3b8"},
  {k:"arms",l:"Biceps/Arms",c:"#f59e0b"},
  {k:"forearms",l:"Forearms",c:"#f97316"},
  {k:"thighs",l:"Thighs",c:"#a78bfa"},
  {k:"calves",l:"Calves",c:"#22c55e"},
];

export function BodyTab({s,d}){
  const { isDesktop } = useLayout();
  const [show,setShow]=useState(false);
  const emptyForm={date:today(),weight:"",bf:"",waist:"",hips:"",chest:"",neck:"",arms:"",thighs:"",calves:"",shoulders:"",forearms:""};
  const [form,setForm]=useState(emptyForm);
  const [showMeasurements,setShowMeasurements]=useState(false);
  const [editingBId,setEditingBId]=useState(null);
  const [chartMetric,setChartMetric]=useState("combo");
  const [expandedB,setExpandedB]=useState(null);
  const [showAllHist,setShowAllHist]=useState(false);
  const [valWarnings,setValWarnings]=useState(null);
  // Track pending timers so we can cancel on unmount (else they fire against a dead component)
  const _timers=useRef([]);
  useEffect(()=>()=>{_timers.current.forEach(clearTimeout);_timers.current=[];},[]);
  const _later=(fn,ms)=>{const id=setTimeout(()=>{ _timers.current=_timers.current.filter(t=>t!==id); fn(); },ms); _timers.current.push(id); return id;};

  const heightM=parseHeightToM(s.profile?.height);
  const mUnit=s.units==="kg"?"cm":"in";

  const openForm=(b)=>{
    if(b){
      const f={date:b.date};
      ["weight","bf","waist","hips","chest","neck","arms","thighs","calves","shoulders","forearms"]
        .forEach(k=>{f[k]=b[k]?b[k].toString():"";});
      setForm(f);
      setEditingBId(b.id);
      setShowMeasurements(MEASUREMENTS.some(m=>parseFloat(b[m.k])>0));
    }else{
      setForm({...emptyForm,date:today()});
      setEditingBId(null);
      setShowMeasurements(false);
    }
    setShow(true);
  };

  const save=()=>{
    const b={id:editingBId||uid(),date:form.date,
      weight:parseFloat(form.weight)||0,bf:parseFloat(form.bf)||0,
      waist:parseFloat(form.waist)||0,hips:parseFloat(form.hips)||0,
      chest:parseFloat(form.chest)||0,neck:parseFloat(form.neck)||0,
      arms:parseFloat(form.arms)||0,thighs:parseFloat(form.thighs)||0,
      calves:parseFloat(form.calves)||0,
      shoulders:parseFloat(form.shoulders)||0,forearms:parseFloat(form.forearms)||0};
    // Sanity-check big jumps (e.g. 20+ unit weight change) before saving
    if(!editingBId){
      const warnings=validateBody(b,s);
      if(warnings.length>0&&!valWarnings){setValWarnings(warnings);return;}
    }
    setValWarnings(null);
    if(editingBId){
      d({type:"EDIT_B",b});
      Haptic.medium();SuccessToastCtrl.show("Entry updated");
      setEditingBId(null);setShow(false);
      return;
    }
    d({type:"ADD_B",b});
    Haptic.medium();SuccessToastCtrl.show("Logged ✓");
    _later(()=>checkAndAwardMissions({...s,body:[b,...s.body]},d),400);
    setShow(false);
    const lastPhotoDate=s.photos[0]?.date;
    const daysSincePhoto=lastPhotoDate?Math.round((Date.now()-new Date(lastPhotoDate+"T12:00:00"))/86400000):99;
    if(daysSincePhoto>=7){
      _later(()=>{
        ConfirmCtrl.show("Take a progress photo?",`It's been ${daysSincePhoto} days since your last one.`,
          ()=>d({type:"TAB",tab:"track_photos"}));
      },500);
    }
  };

  // s.body order can flip after a cloud merge/import — never trust index 0 to be newest
  const sortedBody=useMemo(()=>[...s.body].sort((a,b)=>b.date.localeCompare(a.date)),[s.body]);
  const latest=sortedBody[0]; const prev=sortedBody[1];
  const wc=latest&&prev?(parseFloat(latest.weight)||0)-(parseFloat(prev.weight)||0):0;

  // Chart data for the selected metric over the active range
  const inRange=useMemo(()=>s.body.filter(m=>m.date>=ago(s.range)).sort((a,b)=>a.date.localeCompare(b.date)),[s.body,s.range]);
  const combo=useMemo(()=>inRange.map(m=>({date:fmtShort(m.date),wt:+(parseFloat(m.weight)||0).toFixed(1),bf:+((parseFloat(m.bf)||0).toFixed(1))})),[inRange]);
  const metricSeries=useMemo(()=>{
    if(chartMetric==="combo")return null;
    return inRange.map(m=>({date:fmtShort(m.date),v:+(parseFloat(m[chartMetric])||0)})).filter(p=>p.v>0);
  },[inRange,chartMetric]);
  // Which metrics have enough data to chart
  const chartOptions=useMemo(()=>{
    const opts=[{k:"combo",l:"Wt + BF%",c:V.purple}];
    if(s.body.filter(b=>parseFloat(b.weight)>0).length>=2)opts.push({k:"weight",l:"Weight",c:V.purple});
    if(s.body.filter(b=>parseFloat(b.bf)>0).length>=2)opts.push({k:"bf",l:"BF%",c:V.danger});
    MEASUREMENTS.forEach(m=>{
      if(s.body.filter(b=>parseFloat(b[m.k])>0).length>=2)opts.push({k:m.k,l:m.l,c:m.c});
    });
    return opts;
  },[s.body]);
  const activeOpt=chartOptions.find(o=>o.k===chartMetric)||chartOptions[0];

  // Navy estimate from the latest entry that has the needed tape measurements
  const navyEst=useMemo(()=>{
    const src=s.body.find(b=>parseFloat(b.waist)>0&&parseFloat(b.neck)>0);
    if(!src)return null;
    const est=navyBF({sex:s.profile?.sex,units:s.units,waist:src.waist,neck:src.neck,hips:src.hips,heightM});
    return est?{est,date:src.date}:null;
  },[s.body,s.profile?.sex,s.units,heightM]);

  // Live Navy estimate inside the form as you type measurements
  const formNavy=navyBF({sex:s.profile?.sex,units:s.units,waist:form.waist,neck:form.neck,hips:form.hips,heightM});

  // Use the newest entry that has a weight, so a measurement-only day doesn't hide the energy card
  const energy=calcEnergy(s.profile,sortedBody.find(b=>parseFloat(b.weight)>0)?.weight,s.units);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?700:"100%",margin:isDesktop?"0 auto":0}}>
      <div style={{display:"flex",gap:8}}>
        <Btn full onClick={()=>openForm(s.body.find(b=>b.date===today())||null)}>
          {Icons.plus({size:16,color:V.bg})} Log Today
        </Btn>
        {s.body.find(b=>b.date===ago(1))&&(
          <Btn v="secondary" onClick={()=>{
            const yd=s.body.find(b=>b.date===ago(1));
            if(yd){openForm(yd);setEditingBId(null);setForm(f=>({...f,date:today()}));}
          }} s={{flexShrink:0,fontSize:11}}>{Icons.copy({size:14,color:V.text2})} Yesterday</Btn>
        )}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div style={{gridColumn:"1/-1"}}>
          <Stat icon={Icons.scale} label="Weight" value={latest?.weight?(parseFloat(latest.weight)||0).toFixed(1):"—"} unit={wUnit(s.units)} color={V.purple}
            sub={wc!==0?`${wc>0?"↗ +":"↘ "}${wc.toFixed(1)} ${wUnit(s.units)}`:latest?"→ stable":undefined}/>
          {s.goals?.goalWeight&&latest?.weight&&(()=>{
            const cur=parseFloat(latest.weight)||0;const goal=parseFloat(s.goals.goalWeight)||0;
            // Explicitly take the EARLIEST entry as the starting weight rather than
            // relying on the storage order — order can flip after a cloud merge/import.
            const ascByDate=[...s.body].sort((a,b)=>a.date.localeCompare(b.date));
            const startEntry=ascByDate.find(b=>parseFloat(b.weight)>0);
            const start=startEntry?parseFloat(startEntry.weight)||cur:cur;
            if(!goal||goal===cur)return null;
            const losing=goal<start;const total=Math.abs(start-goal);const done=Math.abs(cur-start);
            const pct=total>0?Math.min(100,Math.round(done/total*100)):0;
            const remaining=Math.abs(goal-cur).toFixed(1);
            return(
              <div style={{marginTop:6,padding:"8px 10px",borderRadius:8,background:V.card}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:11,color:V.text3}}>Goal: {goal}{wUnit(s.units)}</span>
                  <span style={{fontSize:11,fontWeight:700,color:V.accent}}>{pct}% there · {remaining}{wUnit(s.units)} to go</span>
                </div>
                <div style={{height:5,borderRadius:3,background:V.hoverBg}}>
                  <div style={{height:5,borderRadius:3,width:`${pct}%`,
                    background:losing?"linear-gradient(90deg,#f43f5e,#f97316)":"linear-gradient(90deg,#22c55e,#86efac)",
                    transition:"width .5s"}}/>
                </div>
              </div>
            );
          })()}
        </div>
        <Stat icon={Icons.activity} label="Body Fat" value={latest?.bf?(parseFloat(latest.bf)||0).toFixed(1):navyEst?navyEst.est.toFixed(1):"—"} unit="%" color={V.danger}
          sub={(()=>{
            if(!latest?.bf&&navyEst)return"Navy estimate";
            const prevBf=prev?parseFloat(prev.bf)||0:0;const curBf=latest?parseFloat(latest.bf)||0:0;const d2=curBf-prevBf;
            return d2!==0&&prevBf>0?`${d2>0?"↗ +":"↘ "}${d2.toFixed(1)}%`:curBf>0?"→ stable":undefined;
          })()}/>
        <Stat icon={Icons.target} label="Lean Mass" color={V.accent}
          value={(()=>{
            const w=parseFloat(latest?.weight)||0;const bf=parseFloat(latest?.bf)||navyEst?.est||0;
            if(!w||!bf)return"—";
            return(w*(1-bf/100)).toFixed(1);
          })()}
          unit={wUnit(s.units)}
          sub={latest?.bf?undefined:navyEst?"from Navy est.":undefined}/>
      </div>

      {/* #18: Weekly average weight */}
      {s.body.length>=3&&(()=>{
        const last7=s.body.filter(b=>b.date>=ago(7)&&parseFloat(b.weight)>0);
        const last14=s.body.filter(b=>b.date>=ago(14)&&b.date<ago(7)&&parseFloat(b.weight)>0);
        const avg7=last7.length?last7.reduce((a,b2)=>a+parseFloat(b2.weight),0)/last7.length:0;
        const avg14=last14.length?last14.reduce((a,b2)=>a+parseFloat(b2.weight),0)/last14.length:0;
        const weekDiff=avg7&&avg14?avg7-avg14:0;
        if(!avg7)return null;
        // Rate-of-change guidance: >1% bodyweight/week is aggressive either direction
        const pctRate=avg7>0?Math.abs(weekDiff)/avg7*100:0;
        return(
          <Card style={{padding:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:V.text3}}>7-DAY AVERAGE</div>
                <div style={{fontSize:20,fontWeight:800,color:V.purple,fontFamily:V.mono}}>{avg7.toFixed(1)} <span style={{fontSize:11,color:V.text3}}>{wUnit(s.units)}</span></div>
              </div>
              {weekDiff!==0&&<div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:V.text3}}>vs prior week</div>
                <div style={{fontSize:14,fontWeight:700,color:weekDiff>0?V.warn:V.accent,fontFamily:V.mono}}>{weekDiff>0?"+":""}{weekDiff.toFixed(1)}</div>
                <div style={{fontSize:11,color:pctRate>1?V.danger:V.text3}}>{pctRate.toFixed(1)}%/wk{pctRate>1?" · fast":""}</div>
              </div>}
            </div>
          </Card>
        );
      })()}

      {/* ── T4 #18: Body weight trend prediction ── */}
      {(()=>{
        const wtPts=s.body.filter(b=>parseFloat(b.weight)>0)
          .sort((a,b)=>a.date.localeCompare(b.date));
        if(wtPts.length<4)return null;
        // Simple linear regression over last 14 entries (or all if fewer)
        const pts=wtPts.slice(-14);
        const n=pts.length;
        const xs=pts.map((_,i)=>i);
        const ys=pts.map(p=>parseFloat(p.weight));
        const xMean=xs.reduce((a,x)=>a+x,0)/n;
        const yMean=ys.reduce((a,y)=>a+y,0)/n;
        const slope=xs.reduce((a,x,i)=>a+(x-xMean)*(ys[i]-yMean),0)/
                    xs.reduce((a,x)=>a+(x-xMean)**2,0);
        const intercept=yMean-slope*xMean;
        // Days between latest two entries
        const daysBetween=pts.length>=2?
          Math.round((new Date(pts[pts.length-1].date)-new Date(pts[pts.length-2].date))/86400000):1;
        const stepsToProject=Math.round(30/Math.max(daysBetween,1));
        const proj30=parseFloat((intercept+slope*(n-1+stepsToProject)).toFixed(1));
        const proj60=parseFloat((intercept+slope*(n-1+stepsToProject*2)).toFixed(1));
        const current=ys[ys.length-1];
        const delta30=parseFloat((proj30-current).toFixed(1));
        const delta60=parseFloat((proj60-current).toFixed(1));
        if(Math.abs(slope)<0.001)return null; // flat — not interesting
        const goalWeight=parseFloat(s.goals?.goalWeight)||0;
        // Days to goal — require a positive day spacing so projection has meaning
        let daysToGoal=null;
        if(goalWeight>0&&Math.abs(delta30)>0.05&&daysBetween>0){
          const stepsNeeded=(goalWeight-intercept)/slope-(n-1);
          const daysNeeded=Math.round(stepsNeeded*daysBetween);
          if(daysNeeded>0&&daysNeeded<730)daysToGoal=daysNeeded;
        }
        const losing=slope<0;
        const trendColor=losing?V.accent:"#f97316";
        return(
          <div style={{padding:"12px 14px",borderRadius:12,
            background:`linear-gradient(135deg,${trendColor}08,rgba(0,0,0,0))`,
            border:`1px solid ${trendColor}22`}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",
              letterSpacing:".06em",marginBottom:10}}>📉 Trend Projection</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:daysToGoal?10:0}}>
              {[
                {label:"30-day projection",val:proj30,delta:delta30},
                {label:"60-day projection",val:proj60,delta:delta60},
              ].map(p=>(
                <div key={p.label} style={{padding:"9px 10px",borderRadius:10,
                  background:V.card,border:`1px solid ${V.cardBorder}`}}>
                  <div style={{fontSize:11,color:V.text3,marginBottom:4}}>{p.label}</div>
                  <div style={{fontSize:18,fontWeight:800,color:trendColor,fontFamily:V.mono,lineHeight:1}}>
                    {p.val} <span style={{fontSize:11}}>{wUnit(s.units)}</span>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,marginTop:3,
                    color:losing?(p.delta<0?V.accent:V.danger):(p.delta>0?V.accent:"#f97316")}}>
                    {p.delta>0?"+":""}{p.delta} {wUnit(s.units)}
                  </div>
                </div>
              ))}
            </div>
            {daysToGoal&&(
              <div style={{padding:"7px 10px",borderRadius:8,
                background:`${V.accent}08`,border:`1px solid ${V.accent}18`,
                fontSize:11,color:V.text2}}>
                🎯 At this rate, you'll reach {goalWeight}{wUnit(s.units)} in ~<strong style={{color:V.accent}}>{daysToGoal} days</strong>
              </div>
            )}
            <div style={{fontSize:11,color:V.text3,marginTop:8,opacity:0.6}}>
              Based on last {pts.length} weigh-ins · update by logging regularly
            </div>
          </div>
        );
      })()}

      {/* ── Multi-metric chart with switcher ── */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:11,color:V.text3,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Trends</span>
          {chartMetric!=="combo"&&metricSeries&&metricSeries.length>=2&&(()=>{
            const diff=metricSeries[metricSeries.length-1].v-metricSeries[0].v;
            return <span style={{fontSize:11,fontWeight:700,fontFamily:V.mono,
              color:diff===0?V.text3:diff<0?V.accent:V.warn}}>
              {diff>0?"+":""}{diff.toFixed(1)} {chartMetric==="bf"?"%":chartMetric==="weight"?wUnit(s.units):mUnit} over {s.range}d
            </span>;
          })()}
        </div>
        {/* Metric switcher */}
        <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:8,marginBottom:6,WebkitOverflowScrolling:"touch"}}>
          {chartOptions.map(o=>(
            <button key={o.k} onClick={()=>setChartMetric(o.k)} style={{padding:"5px 11px",borderRadius:14,fontSize:11,
              fontWeight:700,fontFamily:V.font,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,WebkitTapHighlightColor:"transparent",
              border:`1px solid ${chartMetric===o.k?`${o.c}45`:"transparent"}`,
              background:chartMetric===o.k?`${o.c}16`:V.hoverBg,color:chartMetric===o.k?o.c:V.text3}}>{o.l}</button>
          ))}
        </div>
        {chartMetric==="combo"?(
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={combo}>
              <CartesianGrid {...chartCfg.grid} vertical={false}/>
              <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
              <YAxis yAxisId="w" domain={["dataMin-2","dataMax+2"]} {...chartCfg.axis} width={30}/>
              <YAxis yAxisId="b" orientation="right" domain={["dataMin-1","dataMax+1"]} {...chartCfg.axis} width={28}/>
              <Tooltip {...chartCfg.tip}/>
              {/* #17: Goal weight line */}
              {s.goals?.goalWeight>0&&<ReferenceLine yAxisId="w" y={s.goals.goalWeight} stroke={V.accent} strokeDasharray="6 3" strokeOpacity={0.6}
                label={{value:`Goal: ${s.goals.goalWeight}`,position:"insideTopRight",fill:V.accent,fontSize:11}}/>}
              <Line yAxisId="w" type="monotone" dataKey="wt" stroke={V.purple} strokeWidth={2} dot={{r:3}} name="Weight"/>
              <Line yAxisId="b" type="monotone" dataKey="bf" stroke={V.danger} strokeWidth={2} dot={{r:3}} name="BF%"/>
            </LineChart>
          </ResponsiveContainer>
        ):(
          metricSeries&&metricSeries.length>=2?(
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={metricSeries}>
                <defs>
                  <linearGradient id="bodyMetricFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={activeOpt.c} stopOpacity={0.25}/>
                    <stop offset="100%" stopColor={activeOpt.c} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid {...chartCfg.grid} vertical={false}/>
                <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
                <YAxis domain={["dataMin-1","dataMax+1"]} {...chartCfg.axis} width={34}/>
                <Tooltip {...chartCfg.tip}/>
                {chartMetric==="weight"&&s.goals?.goalWeight>0&&<ReferenceLine y={s.goals.goalWeight} stroke={V.accent} strokeDasharray="6 3" strokeOpacity={0.6}/>}
                <Area type="monotone" dataKey="v" stroke={activeOpt.c} strokeWidth={2} fill="url(#bodyMetricFill)" dot={{r:3}} name={activeOpt.l}/>
              </AreaChart>
            </ResponsiveContainer>
          ):(
            <div style={{textAlign:"center",padding:30,color:V.text3,fontSize:11}}>
              Log {activeOpt.l.toLowerCase()} on at least 2 days within the range to see a trend
            </div>
          )
        )}
      </Card>

      {/* ── Energy: BMR / TDEE ── */}
      {energy&&(
        <Card style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <SectionTitle icon={Icons.flame} color={V.warn} size={12}>Daily energy</SectionTitle>
            <span style={{fontSize:11,color:V.text3}}>Mifflin-St Jeor · {energy.activity} ({energy.mult}×)</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            {[
              {l:"BMR (at rest)",v:energy.bmr,c:V.accent2},
              {l:"TDEE (maintain)",v:energy.tdee,c:V.warn},
            ].map(x=>(
              <div key={x.l} style={{padding:"9px 10px",borderRadius:10,background:V.card,border:`1px solid ${V.cardBorder}`}}>
                <div style={{fontSize:11,color:V.text3,marginBottom:3}}>{x.l}</div>
                <div style={{fontSize:18,fontWeight:800,color:x.c,fontFamily:V.mono,lineHeight:1}}>{x.v.toLocaleString()} <span style={{fontSize:11}}>kcal</span></div>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:V.text3,marginBottom:8}}>Tap a target to set it as your calorie goal:</div>
          <div style={{display:"flex",gap:6}}>
            {[
              {l:"Cut",v:energy.tdee-500,c:"#f43f5e",sub:"-500"},
              {l:"Maintain",v:energy.tdee,c:V.accent2,sub:"±0"},
              {l:"Bulk",v:energy.tdee+300,c:"#22c55e",sub:"+300"},
            ].map(t=>(
              <button key={t.l} onClick={()=>{
                d({type:"GOALS",g:{cal:t.v,tdee:energy.tdee}});
                Haptic.light();SuccessToastCtrl.show(`Calorie goal set to ${t.v.toLocaleString()}`);
              }} style={{flex:1,padding:"8px 4px",borderRadius:10,cursor:"pointer",
                background:s.goals?.cal===t.v?`${t.c}16`:V.card,
                border:`1px solid ${s.goals?.cal===t.v?`${t.c}45`:V.cardBorder}`,
                WebkitTapHighlightColor:"transparent"}}>
                <div style={{fontSize:13,fontWeight:800,color:t.c,fontFamily:V.mono,lineHeight:1}}>{t.v.toLocaleString()}</div>
                <div style={{fontSize:11,color:V.text3,marginTop:2}}>{t.l} ({t.sub})</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* ── Navy body-fat estimate ── */}
      {navyEst&&(
        <Card style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <SectionTitle icon={Icons.scale} color={V.accent2} size={12} style={{marginBottom:4}}>Navy body-fat estimate</SectionTitle>
              <div style={{fontSize:11,color:V.text3}}>From waist/neck{(s.profile?.sex||"").toLowerCase().startsWith("f")?"/hips":""} tape ({fmtShort(navyEst.date)})</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:24,fontWeight:900,color:V.accent2,fontFamily:V.mono,lineHeight:1}}>{navyEst.est}%</div>
              {latest?.bf>0&&<div style={{fontSize:11,color:V.text3,marginTop:2}}>logged: {(parseFloat(latest.bf)||0).toFixed(1)}%</div>}
            </div>
          </div>
        </Card>
      )}

      {/* FFMI / BMI / Lean Mass */}
      {(()=>{
        const latestW=(s.body||[]).find(b=>b.weight);
        if(!latestW?.weight)return null;
        const bf=parseFloat(latestW.bf)||navyEst?.est||0;
        if(!bf)return null;
        const hm=heightM;
        if(!hm||hm<1)return null;
        const wKg=s.units==="kg"?parseFloat(latestW.weight):parseFloat(latestW.weight)*0.453592;
        if(!wKg||!isFinite(wKg)||!isFinite(bf))return null;
        const leanKg=wKg*(1-bf/100);
        const ffmi=Math.round((leanKg/(hm*hm))*10)/10;
        const bmi=Math.round((wKg/(hm*hm))*10)/10;
        const leanDisp=s.units==="kg"?`${Math.round(leanKg*10)/10} kg`:`${Math.round(leanKg*2.205)} lbs`;
        const ffmiRating=ffmi<18?"Below Avg":ffmi<20?"Average":ffmi<22?"Above Avg":ffmi<24?"Excellent":ffmi<26?"Elite":"World Class";
        const ffmiColor=ffmi<18?V.text3:ffmi<20?V.warn:ffmi<22?V.accent2:ffmi<24?V.accent:ffmi<26?"#f59e0b":"#f43f5e";
        const bmiNote=bmi<18.5?"Underweight":bmi<25?"Normal":bmi<30?"Overweight":"Obese";
        return(
          <Card style={{padding:14}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:10,textTransform:"uppercase",letterSpacing:".06em"}}>Body Composition</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
              {[
                {label:"FFMI",value:ffmi,note:ffmiRating,color:ffmiColor},
                {label:"BMI",value:bmi,note:bmiNote,color:V.text2},
                {label:"Lean Mass",value:leanDisp,note:latestW.bf?"fat-free":"via Navy est",color:V.purple},
              ].map(m=>(
                <div key={m.label} style={{textAlign:"center",padding:"8px 4px",borderRadius:8,
                  background:V.card,border:`1px solid ${V.cardBorder}`}}>
                  <div style={{fontSize:11,color:V.text3,marginBottom:3,textTransform:"uppercase",letterSpacing:".06em"}}>{m.label}</div>
                  <div style={{fontSize:m.label==="Lean Mass"?13:18,fontWeight:900,color:m.color,fontFamily:V.mono,lineHeight:1}}>{m.value}</div>
                  <div style={{fontSize:11,color:m.color,marginTop:3,fontWeight:600}}>{m.note}</div>
                </div>
              ))}
            </div>
            <div style={{padding:"7px 10px",borderRadius:8,background:`${ffmiColor}08`,border:`1px solid ${ffmiColor}18`,fontSize:11,color:V.text2,lineHeight:1.5}}>
              FFMI measures muscle per height. Natural range 18–25 · Elite natural ~25. Requires body fat % (logged or Navy-estimated).
            </div>
          </Card>
        );
      })()}

      {/* #19: Measurement comparison card — since Day 1 */}
      {s.body.length>=2&&(()=>{
        // Sort ascending by date rather than trusting array order (can flip after a
        // cloud merge/import) so "Day 1" → latest endpoints are correct.
        const ascByDate=[...s.body].sort((a,b)=>a.date.localeCompare(b.date));
        const first=ascByDate[0];const last=ascByDate[ascByDate.length-1];
        const changes=[
          {l:"Weight",k:"weight",u:wUnit(s.units),shrinkGood:false},
          {l:"Body Fat",k:"bf",u:"%",shrinkGood:true},
          {l:"Waist",k:"waist",u:mUnit,shrinkGood:true},
          {l:"Hips",k:"hips",u:mUnit,shrinkGood:true},
          {l:"Chest",k:"chest",u:mUnit,shrinkGood:false},
          {l:"Shoulders",k:"shoulders",u:mUnit,shrinkGood:false},
          {l:"Arms",k:"arms",u:mUnit,shrinkGood:false},
          {l:"Forearms",k:"forearms",u:mUnit,shrinkGood:false},
          {l:"Thighs",k:"thighs",u:mUnit,shrinkGood:false},
          {l:"Calves",k:"calves",u:mUnit,shrinkGood:false},
        ].map(c2=>({...c2,v1:parseFloat(first[c2.k])||0,v2:parseFloat(last[c2.k])||0}))
         .filter(c2=>c2.v1>0&&c2.v2>0);
        if(changes.length===0)return null;
        const days=Math.round((new Date(last.date)-new Date(first.date))/86400000);
        return(
          <Card style={{padding:14}}>
            <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:4}}>Progress Since Day 1</div>
            <div style={{fontSize:11,color:V.text3,marginBottom:8}}>{fmtShort(first.date)} → {fmtShort(last.date)} ({days} days)</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {changes.map(c2=>{
                const diff=c2.v2-c2.v1;
                const good=c2.shrinkGood?diff<0:diff>0;
                return(
                  <div key={c2.l} style={{textAlign:"center",padding:8,borderRadius:8,background:V.card}}>
                    <div style={{fontSize:14,fontWeight:800,color:diff===0?V.text3:good?V.accent:V.danger,fontFamily:V.mono}}>
                      {diff>0?"+":""}{diff.toFixed(1)}
                    </div>
                    <div style={{fontSize:11,color:V.text3}}>{c2.l} ({c2.u})</div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {/* Latest measurements grid */}
      {latest&&(()=>{
        const meas=MEASUREMENTS.map(m=>({...m,v:latest[m.k]})).filter(m=>parseFloat(m.v)>0);
        if(meas.length===0)return null;
        return(
          <Card style={{padding:14}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>Measurements</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {meas.map(m=>{
                // Last change for this measurement
                const prevEntry=s.body.find(b=>b.date<latest.date&&parseFloat(b[m.k])>0);
                const delta=prevEntry?(parseFloat(latest[m.k])||0)-(parseFloat(prevEntry[m.k])||0):0;
                return(
                  <div key={m.l} style={{padding:"10px 12px",borderRadius:10,background:V.card,border:`1px solid ${m.c}20`}}>
                    <div style={{fontSize:11,color:V.text3,fontWeight:700,marginBottom:3,textTransform:"uppercase",letterSpacing:".06em"}}>{m.l}</div>
                    <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                      <div style={{fontSize:20,fontWeight:800,color:m.c,fontFamily:V.mono,lineHeight:1}}>{(parseFloat(m.v)||0).toFixed(1)}<span style={{fontSize:11,fontWeight:400,color:V.text3}}> {mUnit}</span></div>
                      {delta!==0&&<span style={{fontSize:11,fontFamily:V.mono,color:V.text3}}>{delta>0?"+":""}{delta.toFixed(1)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {show&&(
        <Sheet title={editingBId?"Edit Entry":"Log Body"} onClose={()=>{setShow(false);setEditingBId(null);}}
          footer={<Btn full onClick={save}>{Icons.check({size:16,color:onAccent()})} {editingBId?"Update":"Save"}</Btn>}>
          <Field label="Date" type="date" value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Essential</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <Field label="Weight" type="number" value={form.weight} onChange={v=>setForm(f=>({...f,weight:v}))} unit={wUnit(s.units)} step="0.1"/>
              <Field label="Body Fat %" type="number" value={form.bf} onChange={v=>setForm(f=>({...f,bf:v}))} unit="%" step="0.1"/>
            </div>
            {/* Live Navy estimate — offer to fill BF% from tape measurements */}
            {formNavy&&!parseFloat(form.bf)&&(
              <button onClick={()=>setForm(f=>({...f,bf:formNavy.toString()}))}
                style={{width:"100%",padding:"9px 12px",borderRadius:10,background:`${V.accent2}08`,
                  border:`1px solid ${V.accent2}25`,cursor:"pointer",textAlign:"left",
                  display:"flex",justifyContent:"space-between",alignItems:"center",fontFamily:V.font}}>
                <span style={{fontSize:11,color:V.text2}}>⚓ Navy estimate from your tape measurements</span>
                <span style={{fontSize:13,fontWeight:800,color:V.accent2,fontFamily:V.mono}}>{formNavy}% → use</span>
              </button>
            )}
          </div>
          <button onClick={()=>setShowMeasurements(v=>!v)}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,background:V.card,
              border:`1px dashed ${showMeasurements?V.accent+"40":track()}`,
              cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center",
              fontFamily:V.font,marginBottom:showMeasurements?10:0}}>
            <span style={{fontSize:12,fontWeight:600,color:showMeasurements?V.accent:V.text3}}>📏 Tape Measurements <span style={{fontSize:11,fontWeight:400,color:V.text3}}>(optional)</span></span>
            <span style={{fontSize:14,color:V.text3}}>{showMeasurements?"▲":"▼"}</span>
          </button>
          {showMeasurements&&(
            <React.Fragment>
              <div style={{fontSize:11,color:V.text3,marginBottom:8,lineHeight:1.5}}>
                Measure relaxed, same time of day. Waist at navel, neck below the Adam's apple, arms flexed at the peak.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {MEASUREMENTS.map(m=>(
                  <Field key={m.k} label={m.l} type="number" value={form[m.k]} onChange={v=>setForm(f=>({...f,[m.k]:v}))} unit={mUnit} step="0.1"/>
                ))}
              </div>
            </React.Fragment>
          )}
          {valWarnings&&<ValidationWarning warnings={valWarnings} onConfirm={()=>{setValWarnings(null);save();}} onCancel={()=>setValWarnings(null)}/>}
        </Sheet>
      )}

      {/* History — expandable rows with edit */}
      <Card>
        <span style={{fontSize:11,color:V.text3,fontWeight:700,display:"block",marginBottom:10,textTransform:"uppercase",letterSpacing:".06em"}}>History</span>
        {s.body.slice(0,showAllHist?200:10).map(m=>{
          const expanded=expandedB===m.id;
          const meas=MEASUREMENTS.map(x=>({...x,v:parseFloat(m[x.k])||0})).filter(x=>x.v>0);
          return(
            <div key={m.id} style={{borderBottom:`1px solid ${V.divider}`}}>
              <div onClick={()=>setExpandedB(expanded?null:m.id)} role="button" tabIndex={0}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:12,color:V.text3}}>{fmtShort(m.date)}</span>
                  {meas.length>0&&<span style={{fontSize:11,fontWeight:700,padding:"1px 5px",borderRadius:4,background:`${V.accent2}10`,color:V.accent2}}>📏 {meas.length}</span>}
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:13,color:V.purple,fontFamily:V.mono}}>{(parseFloat(m.weight)||0).toFixed(1)}</span>
                  <span style={{fontSize:12,color:V.danger,fontFamily:V.mono}}>{((parseFloat(m.bf)||0).toFixed(1))}%</span>
                  <button onClick={e=>{e.stopPropagation();openForm(m);}} aria-label="Edit measurement"
                    style={{background:"none",border:"none",padding:4,cursor:"pointer",fontSize:11,color:V.text3}}>✏️</button>
                  <button onClick={e=>{e.stopPropagation();ConfirmCtrl.show("Delete measurement?",fmtShort(m.date),()=>{Undo.set("Measurement deleted",m,"body");d({type:"DEL_B",id:m.id});SuccessToastCtrl.show("Measurement deleted");});}} aria-label="Delete measurement" style={{background:"none",border:"none",padding:4,cursor:"pointer"}}>{Icons.trash({size:12,color:V.text3})}</button>
                </div>
              </div>
              {expanded&&meas.length>0&&(
                <div style={{display:"flex",gap:6,flexWrap:"wrap",paddingBottom:10}}>
                  {meas.map(x=>(
                    <span key={x.k} style={{fontSize:11,fontFamily:V.mono,padding:"3px 8px",borderRadius:6,
                      background:V.card,border:`1px solid ${x.c}22`,color:x.c}}>
                      {x.l}: <strong>{x.v.toFixed(1)}</strong>{mUnit}
                    </span>
                  ))}
                </div>
              )}
              {expanded&&meas.length===0&&(
                <div style={{fontSize:11,color:V.text3,fontStyle:"italic",paddingBottom:10}}>No tape measurements this entry</div>
              )}
            </div>
          );
        })}
        {s.body.length>10&&!showAllHist&&(
          <button onClick={()=>setShowAllHist(true)} style={{width:"100%",padding:10,marginTop:6,background:"none",
            border:`1px dashed ${V.cardBorder}`,borderRadius:10,cursor:"pointer",fontSize:11,color:V.accent,fontWeight:600,fontFamily:V.font}}>
            Show all {s.body.length} entries
          </button>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
//  ANALYTICS TAB
// ═══════════════════════════════════════
